/**
 * InefficiencyHotspots — Wave 6.2 RESOLUTION-METRICS-KPI
 *
 * Sortable table of officers whose Cancelled rate exceeds the admin-configured
 * `metrics.cancelledRate.alertThreshold` (default 0.25 = 25%, sourced from
 * SystemSettings — NEVER hardcoded; threshold value is surfaced in the panel
 * header so admin tuning shows up immediately).
 *
 * Click an officer row → drill to /metrics/carts?range=…&officer=…&resolution=Cancelled
 *
 * The `restorableCount` column reflects per-(origin × resolutionCode) retention
 * windows once the companion `CartRetentionPolicies` table ships.  Until then
 * we coalesce to the cancelled-count as a conservative ceiling.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type {
  CartResolutionByOfficerRow,
  CartResolutionInefficiencySignals,
  CartResolutionRange,
  CartResolutionThresholds,
} from '@retail-ops/shared-types';

interface InefficiencyHotspotsProps {
  range: CartResolutionRange;
  officers: ReadonlyArray<CartResolutionByOfficerRow>;
  signals: CartResolutionInefficiencySignals;
  thresholds: CartResolutionThresholds;
  branchFilter?: string;
}

export function InefficiencyHotspots({
  range,
  officers,
  signals,
  thresholds,
  branchFilter,
}: InefficiencyHotspotsProps): JSX.Element {
  const navigate = useNavigate();

  const threshold = thresholds.cancelledRateAlert;
  const thresholdPct = `${Math.round(threshold * 100)}%`;

  // Officers above threshold (with min sample size to avoid noise).
  const flagged = useMemo(() => {
    return officers
      .filter((o) => o.total >= 3 && o.cancelledRate > threshold)
      .sort((a, b) => b.cancelledRate - a.cancelledRate);
  }, [officers, threshold]);

  function drillOfficer(officerCode: string): void {
    const params = new URLSearchParams();
    params.set('range', range);
    params.set('officer', officerCode);
    params.set('resolution', 'Cancelled');
    if (branchFilter && branchFilter !== 'all') params.set('branch', branchFilter);
    navigate(`/metrics/carts?${params.toString()}`);
  }

  function drillStuck(): void {
    const params = new URLSearchParams();
    params.set('range', range);
    params.set('stuck', '1');
    if (branchFilter && branchFilter !== 'all') params.set('branch', branchFilter);
    navigate(`/metrics/carts?${params.toString()}`);
  }

  return (
    <div className="inefficiency-hotspots">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 'var(--sp-4)',
          marginBottom: 'var(--sp-6)',
        }}
      >
        <SummaryStat
          label="Cancelled rate (overall)"
          value={`${(signals.cancelledRateOverall * 100).toFixed(1)}%`}
          tone={signals.cancelledRateOverall > threshold ? 'red' : 'green'}
          alert={signals.cancelledRateOverall > threshold ? `> ${thresholdPct} threshold` : undefined}
        />
        <SummaryStat
          label="Stuck open carts (>24h)"
          value={signals.stuckOpenCarts}
          tone={signals.stuckOpenCarts > 0 ? 'amber' : 'green'}
          onClick={signals.stuckOpenCarts > 0 ? drillStuck : undefined}
        />
        <SummaryStat
          label="Swept vs manual"
          value={`${signals.sweptVsManualClosed.swept} · ${signals.sweptVsManualClosed.manual}`}
          tone="default"
        />
      </div>

      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-4)',
          marginBottom: 'var(--sp-4)',
        }}
      >
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--ink-900)' }}>
          Officers above {thresholdPct} Cancelled rate
        </h4>
        <span style={{ fontSize: 10, color: 'var(--ink-500)' }}>
          (threshold from SystemSettings · admin-tunable)
        </span>
      </header>

      {flagged.length === 0 ? (
        <div
          style={{
            padding: 'var(--sp-7)',
            fontSize: 12,
            color: 'var(--pcb-green)',
            background: 'var(--ok-bg, #f0f9f0)',
            borderRadius: 'var(--r-md)',
            textAlign: 'center',
          }}
        >
          No officers exceed the threshold in this window.
        </div>
      ) : (
        <table
          className="inefficiency-table"
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
          }}
        >
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--ink-600)' }}>
              <th style={cellStyle}>Officer</th>
              <th style={cellStyle}>Total</th>
              <th style={cellStyle}>Cancelled</th>
              <th style={cellStyle}>Rate</th>
              <th style={cellStyle}>Restorable*</th>
            </tr>
          </thead>
          <tbody>
            {flagged.map((o) => {
              const overshoot = o.cancelledRate - threshold;
              const tone = overshoot > 0.10 ? 'red' : 'amber';
              return (
                <tr
                  key={o.officerCode}
                  style={{
                    borderTop: '1px solid var(--ink-100)',
                    cursor: 'pointer',
                  }}
                  onClick={() => drillOfficer(o.officerCode)}
                >
                  <td style={cellStyle}>
                    <strong>{o.displayName}</strong>{' '}
                    <span style={{ color: 'var(--ink-500)', fontSize: 10 }}>· {o.officerCode}</span>
                  </td>
                  <td style={cellStyle}>{o.total}</td>
                  <td style={cellStyle}>{o.cancelled}</td>
                  <td style={{ ...cellStyle, color: tone === 'red' ? 'var(--pcb-red)' : 'var(--pcb-amber-text)', fontWeight: 700 }}>
                    {(o.cancelledRate * 100).toFixed(1)}%
                  </td>
                  <td style={cellStyle}>
                    {/* `restorableCount` placeholder — companion CartRetentionPolicies
                        landing will replace with a per-cart retention check. */}
                    {o.cancelled}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div style={{ fontSize: 10, color: 'var(--ink-500)', marginTop: 'var(--sp-4)' }}>
        * Restorable = carts whose retention window has not yet expired.
        Final values land with the <code>CartRetentionPolicies</code> table.
      </div>
    </div>
  );
}

function SummaryStat({
  label,
  value,
  tone,
  alert,
  onClick,
}: {
  label: string;
  value: string | number;
  tone: 'red' | 'amber' | 'green' | 'default';
  alert?: string;
  onClick?: () => void;
}): JSX.Element {
  const color =
    tone === 'red' ? 'var(--pcb-red)' :
    tone === 'amber' ? 'var(--pcb-amber-text)' :
    tone === 'green' ? 'var(--pcb-green)' : 'var(--ink-900)';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      style={{
        textAlign: 'left',
        padding: 'var(--sp-4) var(--sp-5)',
        background: 'var(--ink-50)',
        border: '1px solid var(--surface-border)',
        borderRadius: 'var(--r-md)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ fontSize: 10, color: 'var(--ink-600)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, color, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
        {value}
      </div>
      {alert && (
        <div style={{ fontSize: 10, color: 'var(--pcb-red)', marginTop: 2 }}>{alert}</div>
      )}
    </button>
  );
}

const cellStyle: React.CSSProperties = {
  padding: 'var(--sp-3) var(--sp-4)',
  verticalAlign: 'middle',
};
